import { executeQuery } from './salesforce/client';
import { getCached, setCache } from './cache';
import { segmentWhereClause } from './segmentFilter';
import {
  EXCLUDE_DEACTIVATED_ACCOUNTS,
  EXCLUDE_DEACTIVATED_FIRMS,
  EXCLUDE_DEACTIVATED_FIRMS_REL,
  EXCLUDE_DEMO_DEACTIVATED_USERS,
} from '../constants';
import type { AccountSegment } from '../types/segment';
import type {
  MetricDefinition,
  PeriodBucket,
  ComputedMetric,
  PeriodType,
} from '../types/weeklyMetrics';

const CACHE_TTL = 15 * 60 * 1000; // 15 minutes

// ── Date helpers ────────────────────────────────────────────

/**
 * Parse a date string as local time to avoid UTC timezone shift.
 */
function parseLocalDate(value: string): Date {
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const [y, m, d] = value.split('-').map(Number);
    return new Date(y, m - 1, d);
  }
  return new Date(value);
}

function toLocalISODate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

// ── Period key generation ───────────────────────────────────

function getWeekEnding(date: Date): string {
  const d = new Date(date);
  const dayOfWeek = d.getDay();
  const daysUntilSat = 6 - dayOfWeek;
  d.setDate(d.getDate() + daysUntilSat);
  return toLocalISODate(d);
}

function getMonthKey(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  return `${y}-${m}`;
}

function getQuarterKey(date: Date): string {
  const y = date.getFullYear();
  const q = Math.ceil((date.getMonth() + 1) / 3);
  return `${y}-Q${q}`;
}

function getYearKey(date: Date): string {
  return String(date.getFullYear());
}

function getPeriodKey(date: Date, periodType: PeriodType): string {
  const base = getBasePeriodType(periodType);
  switch (base) {
    case 'weekly': return getWeekEnding(date);
    case 'monthly': return getMonthKey(date);
    case 'quarterly': return getQuarterKey(date);
    case 'annual': return getYearKey(date);
  }
}

// ── Period-to-date helpers ──────────────────────────────────

type BasePeriodType = 'weekly' | 'monthly' | 'quarterly' | 'annual';

function getBasePeriodType(pt: PeriodType): BasePeriodType {
  switch (pt) {
    case 'this-week': return 'weekly';
    case 'this-month': return 'monthly';
    case 'this-quarter': return 'quarterly';
    case 'this-year': return 'annual';
    case 'mtd': return 'monthly';
    case 'qtd': return 'quarterly';
    case 'ytd': return 'annual';
    default: return pt;
  }
}

function isToDateType(pt: PeriodType): boolean {
  return pt === 'mtd' || pt === 'qtd' || pt === 'ytd';
}

/** How many days into its period a given date falls (1-based). */
function getDayWithinPeriod(date: Date, base: BasePeriodType): number {
  switch (base) {
    case 'monthly':
      return date.getDate();
    case 'quarterly': {
      const qStart = new Date(date.getFullYear(), Math.floor(date.getMonth() / 3) * 3, 1);
      return Math.floor((date.getTime() - qStart.getTime()) / 86_400_000) + 1;
    }
    case 'annual': {
      const yStart = new Date(date.getFullYear(), 0, 1);
      return Math.floor((date.getTime() - yStart.getTime()) / 86_400_000) + 1;
    }
    default:
      return 0;
  }
}

/** Today's day-within-period, used as the cutoff for to-date comparisons. */
function getPeriodDayCutoff(periodType: PeriodType): number | undefined {
  if (!isToDateType(periodType)) return undefined;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return getDayWithinPeriod(today, getBasePeriodType(periodType));
}

// ── Column generation ───────────────────────────────────────

const PERIOD_COUNTS: Record<PeriodType, number> = {
  'this-week': 12,
  'this-month': 12,
  'this-quarter': 8,
  'this-year': 4,
  weekly: 12,
  monthly: 12,
  quarterly: 8,
  annual: 4,
  mtd: 12,
  qtd: 8,
  ytd: 4,
};

function generateWeeklyColumns(count: number): string[] {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  // Current week's ending Saturday (may be in the future)
  const thisSaturday = new Date(today);
  const dayOfWeek = today.getDay();
  thisSaturday.setDate(thisSaturday.getDate() + (6 - dayOfWeek));

  const columns: string[] = [];
  for (let i = 0; i < count; i++) {
    const sat = new Date(thisSaturday);
    sat.setDate(sat.getDate() - i * 7);
    columns.push(toLocalISODate(sat));
  }
  return columns;
}

function generateMonthlyColumns(count: number): string[] {
  const today = new Date();
  const columns: string[] = [];
  for (let i = 0; i < count; i++) {
    const d = new Date(today.getFullYear(), today.getMonth() - i, 1);
    columns.push(getMonthKey(d));
  }
  return columns;
}

function generateQuarterlyColumns(count: number): string[] {
  const today = new Date();
  const currentQ = Math.ceil((today.getMonth() + 1) / 3);
  const currentY = today.getFullYear();
  const columns: string[] = [];
  for (let i = 0; i < count; i++) {
    let q = currentQ - i;
    let y = currentY;
    while (q <= 0) { q += 4; y--; }
    columns.push(`${y}-Q${q}`);
  }
  return columns;
}

function generateAnnualColumns(count: number): string[] {
  const currentYear = new Date().getFullYear();
  const columns: string[] = [];
  for (let i = 0; i < count; i++) {
    columns.push(String(currentYear - i));
  }
  return columns;
}

/** Base period types that exclude the current (incomplete) period. */
const HISTORICAL_ONLY: Set<PeriodType> = new Set(['weekly', 'monthly', 'quarterly', 'annual']);

export function generatePeriodColumns(periodType: PeriodType): string[] {
  const count = PERIOD_COUNTS[periodType];
  const base = getBasePeriodType(periodType);
  const skipCurrent = HISTORICAL_ONLY.has(periodType);
  const genCount = skipCurrent ? count + 1 : count;

  let columns: string[];
  switch (base) {
    case 'weekly': columns = generateWeeklyColumns(genCount); break;
    case 'monthly': columns = generateMonthlyColumns(genCount); break;
    case 'quarterly': columns = generateQuarterlyColumns(genCount); break;
    case 'annual': columns = generateAnnualColumns(genCount); break;
  }

  return skipCurrent ? columns.slice(1) : columns;
}

// ── Window start date ───────────────────────────────────────

function getWindowStartDate(columns: string[], periodType: PeriodType): string {
  const oldest = columns[columns.length - 1];
  const base = getBasePeriodType(periodType);

  switch (base) {
    case 'weekly': {
      const sat = parseLocalDate(oldest);
      sat.setDate(sat.getDate() - 6);
      return toLocalISODate(sat);
    }
    case 'monthly': {
      // oldest = "YYYY-MM" → first day of that month
      const [y, m] = oldest.split('-').map(Number);
      return toLocalISODate(new Date(y, m - 1, 1));
    }
    case 'quarterly': {
      // oldest = "YYYY-Q1" → first day of that quarter
      const [yStr, qStr] = oldest.split('-Q');
      const startMonth = (Number(qStr) - 1) * 3;
      return toLocalISODate(new Date(Number(yStr), startMonth, 1));
    }
    case 'annual': {
      return `${oldest}-01-01`;
    }
  }
}

// ── Bucketing ───────────────────────────────────────────────

export function bucketByPeriod(
  records: Record<string, unknown>[],
  dateField: string,
  aggregation: 'count' | 'sum' | 'countDistinct' | 'avg',
  valueField: string | undefined,
  columns: string[],
  periodType: PeriodType,
  dateOffsetDays?: number,
  periodDayCutoff?: number,
  distinctField?: string
): PeriodBucket[] {
  const basePeriod = getBasePeriodType(periodType);
  const periodMap = new Map<string, number>();
  // For countDistinct, track unique values per period
  const distinctSets = aggregation === 'countDistinct'
    ? new Map<string, Set<string>>(columns.map((col) => [col, new Set()]))
    : null;
  // For avg, track running count per period so we can divide the summed value.
  const avgCounts = aggregation === 'avg'
    ? new Map<string, number>(columns.map((col) => [col, 0]))
    : null;
  for (const col of columns) {
    periodMap.set(col, 0);
  }

  for (const record of records) {
    const rawDate = record[dateField];
    if (!rawDate) continue;
    const date = parseLocalDate(String(rawDate));
    if (isNaN(date.getTime())) continue;

    if (dateOffsetDays) {
      date.setDate(date.getDate() + dateOffsetDays);
    }

    const key = getPeriodKey(date, periodType);
    if (!periodMap.has(key)) continue;

    // For to-date comparisons, only include records within the first N days of each period
    if (periodDayCutoff != null) {
      const dayInPeriod = getDayWithinPeriod(date, basePeriod);
      if (dayInPeriod > periodDayCutoff) continue;
    }

    if (aggregation === 'count') {
      periodMap.set(key, (periodMap.get(key) || 0) + 1);
    } else if ((aggregation === 'sum' || aggregation === 'avg') && valueField) {
      const val = Number(record[valueField]);
      if (!isNaN(val)) {
        periodMap.set(key, (periodMap.get(key) || 0) + val);
        if (avgCounts) avgCounts.set(key, (avgCounts.get(key) || 0) + 1);
      }
    } else if (aggregation === 'countDistinct' && distinctField) {
      const fieldVal = record[distinctField];
      if (fieldVal != null) {
        distinctSets!.get(key)!.add(String(fieldVal));
      }
    }
  }

  // For countDistinct, convert set sizes to counts
  if (distinctSets) {
    for (const [key, set] of distinctSets) {
      periodMap.set(key, set.size);
    }
  }

  // For avg, divide the summed value by the per-period count (rounded).
  if (avgCounts) {
    for (const [key, count] of avgCounts) {
      periodMap.set(key, count > 0 ? Math.round((periodMap.get(key) || 0) / count) : 0);
    }
  }

  const reversed = [...columns].reverse();
  return reversed.map((col) => ({
    periodKey: col,
    value: periodMap.get(col) || 0,
  }));
}

// ── Change computation ──────────────────────────────────────

export function computeChange(
  current: number | null,
  previous: number | null
): { changePercent: number | null; trend: 'up' | 'down' | 'flat' | null } {
  if (current == null || previous == null) {
    return { changePercent: null, trend: null };
  }
  if (previous === 0) {
    if (current === 0) return { changePercent: 0, trend: 'flat' };
    return { changePercent: 100, trend: 'up' };
  }

  const pct = ((current - previous) / Math.abs(previous)) * 100;
  let trend: 'up' | 'down' | 'flat';
  if (pct > 2) trend = 'up';
  else if (pct < -2) trend = 'down';
  else trend = 'flat';

  return { changePercent: Math.round(pct * 10) / 10, trend };
}

function getEffectiveTrend(
  trend: 'up' | 'down' | 'flat' | null,
  inverted?: boolean
): 'positive' | 'negative' | 'neutral' | null {
  if (trend == null) return null;
  if (trend === 'flat') return 'neutral';
  if (inverted) {
    return trend === 'down' ? 'positive' : 'negative';
  }
  return trend === 'up' ? 'positive' : 'negative';
}

// ── Fetch + compute ─────────────────────────────────────────

async function fetchOneMetric(
  definition: MetricDefinition,
  columns: string[],
  periodType: PeriodType,
  signal?: AbortSignal,
  segment: AccountSegment = 'all'
): Promise<ComputedMetric> {
  let queryStartDate = getWindowStartDate(columns, periodType);
  if (definition.dateOffsetDays) {
    const d = parseLocalDate(queryStartDate);
    d.setDate(d.getDate() - definition.dateOffsetDays);
    queryStartDate = toLocalISODate(d);
  }
  const startDateTime = queryStartDate + 'T00:00:00Z';
  let soql = definition.query
    .replace(/{startDateTime}/g, startDateTime)
    .replace(/{startDate}/g, queryStartDate);

  if (definition.includeDeactivated) {
    // No deactivation filter: these metrics count historical records as-of when they happened.
  } else if (definition.segmentObject === 'Account') {
    soql += EXCLUDE_DEACTIVATED_ACCOUNTS;
  } else if (definition.segmentObject === 'Encore_Firm__c') {
    soql += EXCLUDE_DEACTIVATED_FIRMS;
  } else if (definition.segmentObject === 'Opportunity') {
    // Orders now relate to the firm via Encore_Firm__c, not Account.
    soql += EXCLUDE_DEACTIVATED_FIRMS_REL;
  } else if (definition.segmentObject === 'Contact') {
    soql += EXCLUDE_DEMO_DEACTIVATED_USERS;
  }

  // Inject segment WHERE clause if applicable
  if (segment !== 'all' && definition.segmentObject) {
    const prefix =
      definition.segmentObject === 'Account' || definition.segmentObject === 'Encore_Firm__c'
        ? '' // Total_Plans_Sold__c is directly on the object
        : definition.segmentObject === 'Opportunity'
        ? 'Encore_Firm__r' // Orders traverse to the firm
        : 'Account'; // Contact
    soql += segmentWhereClause(segment, prefix);
  }

  const cacheKey = `metric:${definition.key}:${periodType}:${queryStartDate}:${segment}`;
  const cached = getCached<Record<string, unknown>[]>(cacheKey, CACHE_TTL);
  let records: Record<string, unknown>[];

  if (cached) {
    records = cached;
  } else if (definition.customFetcher) {
    records = await definition.customFetcher(queryStartDate, signal);
    setCache(cacheKey, records);
  } else {
    records = await executeQuery<Record<string, unknown>>(soql, signal);
    setCache(cacheKey, records);
  }

  const periodDayCutoff = getPeriodDayCutoff(periodType);

  const periods = bucketByPeriod(
    records,
    definition.dateField,
    definition.aggregation,
    definition.valueField,
    columns,
    periodType,
    definition.dateOffsetDays,
    periodDayCutoff,
    definition.distinctField
  );

  const currentValue = periods.length > 0 ? periods[periods.length - 1].value : null;
  const previousValue = periods.length > 1 ? periods[periods.length - 2].value : null;
  const { changePercent, trend } = computeChange(currentValue, previousValue);
  const effectiveTrend = getEffectiveTrend(trend, definition.invertedPolarity);

  const trailingAvg =
    periods.length > 0
      ? Math.round((periods.reduce((sum, p) => sum + p.value, 0) / periods.length) * 10) / 10
      : null;

  return {
    definition,
    periods,
    currentValue,
    previousValue,
    changePercent,
    trend,
    effectiveTrend,
    trailingAvg,
  };
}

export async function fetchAllMetrics(
  definitions: MetricDefinition[],
  periodType: PeriodType,
  signal?: AbortSignal,
  segment: AccountSegment = 'all'
): Promise<ComputedMetric[]> {
  const columns = generatePeriodColumns(periodType);
  const active = definitions.filter((d) => d.query.trim() !== '' || d.customFetcher);

  const results: ComputedMetric[] = [];
  const batchSize = 5;

  for (let i = 0; i < active.length; i += batchSize) {
    const batch = active.slice(i, i + batchSize);
    const batchResults = await Promise.all(
      batch.map((def) => fetchOneMetric(def, columns, periodType, signal, segment))
    );
    results.push(...batchResults);
  }

  return results;
}
